/**
 * Lightbox — tam ekran görsel önizleme.
 *
 * `createPortal` ile doğrudan `document.body`'ye render edilir; bu sayede
 * bir Mantine Modal/Drawer içinden açılsa bile o stacking context'e
 * hapsolmaz ve gerçekten tüm ekranı kaplar.
 *
 * Tam-boy görseli gösterir (thumbnail değil); birden fazla görsel varsa
 * ok tuşları / yan butonlarla gezilir. ESC veya arka plana tıklayınca
 * kapanır.
 *
 * Kullanım:
 *   const [idx, setIdx] = useState<number | null>(null);
 *   ...
 *   <Image onClick={() => setIdx(0)} ... />
 *   {idx !== null && (
 *     <Lightbox images={urls} index={idx}
 *               onIndexChange={setIdx} onClose={() => setIdx(null)} />
 *   )}
 */
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ActionIcon, Box, Text } from "@mantine/core";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";

interface LightboxProps {
  /** Tam-boy görsel URL'leri. */
  images: string[];
  /** Gösterilen görselin index'i. */
  index: number;
  /** Index değişince (ok tuşları/butonlar) çağrılır. */
  onIndexChange: (i: number) => void;
  /** Kapatma — ESC, ✕ veya arka plan tıklaması. */
  onClose: () => void;
}

export function Lightbox({ images, index, onIndexChange, onClose }: LightboxProps) {
  const count = images.length;
  const go = (delta: number) => onIndexChange((index + delta + count) % count);

  // Klavye: ESC kapat, ←/→ gez. Lightbox açıkken sayfa kaymasın.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && count > 1) go(1);
      else if (e.key === "ArrowLeft" && count > 1) go(-1);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, count]);

  if (count === 0) return null;

  const overlay = (
    <Box
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        // Mantine Modal z-index'i 200 civarı; lightbox onun da üstünde olmalı.
        zIndex: 10_000,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Kapat */}
      <ActionIcon
        variant="filled"
        color="dark"
        size="lg"
        radius="xl"
        style={{ position: "absolute", top: 16, right: 16 }}
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Kapat"
      >
        <IconX size={20} />
      </ActionIcon>

      {/* Önceki */}
      {count > 1 && (
        <ActionIcon
          variant="filled"
          color="dark"
          size="xl"
          radius="xl"
          style={{ position: "absolute", left: 16 }}
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          aria-label="Önceki görsel"
        >
          <IconChevronLeft size={26} />
        </ActionIcon>
      )}

      {/* Görsel — kendine tıklayınca kapanmaz */}
      <img
        src={images[index]}
        alt={`Görsel ${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "92vw",
          maxHeight: "92vh",
          objectFit: "contain",
          borderRadius: 8,
          boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
        }}
      />

      {/* Sonraki */}
      {count > 1 && (
        <ActionIcon
          variant="filled"
          color="dark"
          size="xl"
          radius="xl"
          style={{ position: "absolute", right: 16 }}
          onClick={(e) => { e.stopPropagation(); go(1); }}
          aria-label="Sonraki görsel"
        >
          <IconChevronRight size={26} />
        </ActionIcon>
      )}

      {/* Sayaç */}
      {count > 1 && (
        <Text
          size="sm"
          c="white"
          style={{
            position: "absolute",
            bottom: 20,
            background: "rgba(0,0,0,0.6)",
            padding: "4px 12px",
            borderRadius: 999,
          }}
        >
          {index + 1} / {count}
        </Text>
      )}
    </Box>
  );

  // Portal ile body'ye — modal stacking context'inden bağımsız tam ekran.
  return createPortal(overlay, document.body);
}
